/**
 * Shared constants for Dogdell Park.
 *
 * Map art is a tile grid for walkability; player poses are continuous.
 */

export const PROTOCOL_VERSION = 2;

export const DEFAULT_ROOM_ID = "hub";

/** Fallback / max pathfinding grid (per-map width/height always wins). */
export const MAP_WIDTH = 80;
export const MAP_HEIGHT = 56;

export const TILE_SIZE = 64;

export const CAMERA_VIEW_TILES_X = 16;
/** RS2-style perspective: pitch is radians above the horizon. */
export const CAMERA_PITCH = 0.78;
export const CAMERA_PITCH_MIN = 0.38;
export const CAMERA_PITCH_MAX = 1.12;
export const CAMERA_YAW = Math.PI * 0.28;
export const CAMERA_DISTANCE = 20;
export const CAMERA_DISTANCE_MIN = 7;
export const CAMERA_DISTANCE_MAX = 24;
export const CAMERA_FOLLOW_RADIUS = 0.05;
export const CAMERA_LERP = 0.14;

export const TICK_HZ = 20;
export const TICK_MS = 1000 / TICK_HZ;

export const SKILL_TICK_MS = 600;
export const SKILL_TICKS_PER_SKILL_TICK = Math.round(SKILL_TICK_MS / TICK_MS);

export const MOVE_SPEED_TILES_PER_SEC = 5.5;
/** Walk → run multiplier (Shift or on-screen Run toggle). */
export const RUN_SPEED_MULT = 1.942;
/** Run stamina (0-100). Drains while jogging; regenerates whenever not actively running. */
export const RUN_ENERGY_MAX = 100;
export const RUN_ENERGY_DRAIN_PER_SEC = 11.2;
export const RUN_ENERGY_REGEN_PER_SEC = 9;
export const ARRIVE_EPSILON = 0.05;
export const TILE_MOVE_MS = 1000 / MOVE_SPEED_TILES_PER_SEC;

export const PACK_SIZE_DEFAULT = 28;
export const INVENTORY_SIZE = PACK_SIZE_DEFAULT;
export const VAULT_SLOTS = 80;
export const MAX_STACK = 99;
export const MAX_LEVEL = 99;

export const CHAT_MAX_LENGTH = 60;
export const CHAT_BUBBLE_MS = 4500;
export const NAME_MAX_LENGTH = 16;

export const INTERACT_RANGE = 2.15;
/** Wandering flavor NPCs roam near home - allow skill interact across their roam. */
export const NPC_WANDER_INTERACT_RANGE = 22;
export const FORGE_RANGE = 14;
export const BANK_RANGE = 3.5;
/** Player / NPC dog breed slots (`color` indexes into the breed pack). */
export const DOG_BREED_COUNT = 6;

/** Clamp a join / cosmetics breed pick to a valid slot. */
export function normalizeBreedColor(raw: unknown): number | null {
  if (typeof raw !== "number" || !Number.isFinite(raw)) {
    return null;
  }
  const n = Math.floor(raw);
  if (n < 0 || n >= DOG_BREED_COUNT) {
    return null;
  }
  return n;
}
export const SOCIAL_FORGE_RADIUS = 25;
export const SOCIAL_FORGE_BONUS_PER_PLAYER = 0.02;
export const SOCIAL_FORGE_BONUS_CAP = 0.15;

export const TELEPORT_COOLDOWN_MS = 2000;
export const TRAVEL_MENU_GRACE_MS = 45000;
export const TELEPORT_INVULN_MS = 1500;

export const PORTAL_DURATION_MS = 60000;
export const PORTAL_RADIUS = 5;
export const PORTAL_CLEARANCE = 14;

export const BOOST_DURATION_MS = 5 * 60 * 1000;
export const BOOST_SPEED_MULT = 0.5;

export const INTERPOLATION_DELAY_MS = 120;
